/**
 * Variant-aware presentation (sections/main-product.liquid, section setting "Variant-aware presentation").
 *
 * The server already renders the blocks, the description, the stock badge and the live region's state for the
 * selected variant, so the first paint and ?variant= links are right without this file. This file follows later
 * switches: on the existing on:variant:change event (fired by <variant-picker>, bubbling) it reads the product's
 * <script type="application/json" data-variant-presentation> ({variantId: {mode, desc, badge}}) and toggles `hidden` on
 *   - [data-presentation-show] blocks (their "Show for": all, regular, sale_item, nearly_gone, not_sale_item, not_regular),
 *   - [data-variant-description] alternatives ("product" = product.description, "d1".. = a variant's own description),
 *   - [data-variant-badge] (LAST ONE LEFT / NEARLY GONE!, per colour, computed by the server),
 * and updates [data-presentation-live], a polite live region that says what a shopper sees change: final sale and the
 * stock badge. Its data-state ("sale|<badge>") is the state it last described; the server sets it for the first variant.
 * Everything is scoped to the event target's closest .js-product, so quick buy works the same way.
 * It never touches the product form, the selected variant, quantity, line-item properties or focus. The title is
 * handled by the listener in variant-picker.js.
 *
 * Loaded with defer only on variant-aware pages, and in the theme editor. Quick buy can add the script again: the
 * guard keeps one listener. Quick buy inserts its content while this file may still be downloading, so a switch made
 * before it runs has no listener: on start it applies each product's current variant once (a no-op when nothing
 * changed since the server render; on a product page the variant picker is not set up yet and it does nothing).
 * Theme editor: a block hidden by its "Show for" is shown while it is selected, so it can be previewed and edited.
 */
if (!window.wsVariantPresentation) {
  window.wsVariantPresentation = true;

  // The same matching as the "Show for" code at the top of the block loop in main-product.liquid.
  const matches = (show, mode) => show === 'all'
    || show === mode
    || (show === 'not_sale_item' && mode !== 'sale_item')
    || (show === 'not_regular' && mode !== 'regular');

  // Live region. The state is "sale|<badge>", as vp_state in main-product.liquid. It is said once when it changes;
  // with neither SALE nor a badge the text is cleared and nothing is said.
  const stateOf = (entry) => `${entry.mode === 'sale_item' ? 'sale' : ''}|${entry.badge || ''}`;
  const SAY_BADGE = { 'LAST ONE LEFT': 'Last one left', 'NEARLY GONE!': 'Nearly gone' };
  const messageOf = (entry) => [
    entry.mode === 'sale_item' ? 'Final sale item' : '',
    entry.badge ? (SAY_BADGE[entry.badge] || entry.badge) : '',
  ].filter(Boolean).join('. ');

  const cache = new WeakMap();
  const readData = (el) => {
    if (!cache.has(el)) {
      let data = null;
      try { data = JSON.parse(el.textContent); } catch (e) { data = null; }
      cache.set(el, data);
    }
    return cache.get(el);
  };

  const setHidden = (el, hidden) => { if (el.hidden !== hidden) el.hidden = hidden; };
  const own = (product, selector) => [...product.querySelectorAll(selector)].filter((el) => el.closest('.js-product') === product);

  const apply = (product, variantId) => {
    const dataEl = own(product, 'script[data-variant-presentation]')[0];
    const data = dataEl && readData(dataEl);
    const entry = data && data[variantId];
    if (!entry) return;

    own(product, '[data-presentation-show]').forEach((el) => {
      delete el.dataset.presentationPreview;
      setHidden(el, !matches(el.dataset.presentationShow, entry.mode));
    });

    const desc = entry.desc || 'product';
    own(product, '[data-variant-description]').forEach((el) => setHidden(el, el.dataset.variantDescription !== desc));

    own(product, '[data-variant-badge]').forEach((el) => {
      const label = el.firstElementChild || el;
      if (entry.badge && label.textContent !== entry.badge) label.textContent = entry.badge;
      setHidden(el, !entry.badge);
    });

    const state = stateOf(entry);
    own(product, '[data-presentation-live]').forEach((el) => {
      if (el.dataset.state === state) return;
      el.dataset.state = state;
      el.textContent = messageOf(entry);
    });
  };

  document.addEventListener('on:variant:change', (evt) => {
    const variant = evt.detail && evt.detail.variant;
    const product = evt.target.closest && evt.target.closest('.js-product');
    if (!variant || !product) return;
    apply(product, variant.id);
  });

  // Start: the current variant of every product whose variant picker is already set up (quick buy).
  document.querySelectorAll('script[data-variant-presentation]').forEach((dataEl) => {
    const product = dataEl.closest('.js-product');
    const picker = product && own(product, 'variant-picker')[0];
    if (picker && picker.hasAttribute('loaded') && picker.variant) apply(product, picker.variant.id);
  });

  // Theme editor only (these events never fire on the storefront).
  document.addEventListener('shopify:block:select', (evt) => {
    const el = evt.target.closest && evt.target.closest('[data-presentation-show]');
    if (!el || !el.hidden) return;
    el.dataset.presentationPreview = '';
    el.hidden = false;
  });
  document.addEventListener('shopify:block:deselect', (evt) => {
    const el = evt.target.closest && evt.target.closest('[data-presentation-preview]');
    if (!el) return;
    delete el.dataset.presentationPreview;
    el.hidden = true;
  });
}
